/**
 * BMR Details (/batches) — the page's parts in the CPV visual language (EXV-007 / 015 / 017 / 018 / 027 / 031).
 *
 *   useBmrdVars        the theme tokens as CSS variables (light and dark follow the active theme)
 *   BatchStateTiles    the summary cards: label, value, definition and link exactly as the server sends them
 *   BatchListFilters   MBMR (light id/name list), schedule-date range and search — shared by every list tab
 *   BatchRowActions    44 px icon actions (named by tooltip and aria-label): View record, View workflow, and Terminate under ⋮
 *   TerminateBatchModal one dialog: reason + e-signature, one signed request; a refusal shown inline with its code
 */
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, DatePicker, Dropdown, Input, Select, Skeleton, Tooltip, theme } from 'antd';
import { Activity, CalendarClock, CheckCheck, Eye, MoreVertical, PauseCircle, StopCircle, Workflow, XCircle } from 'lucide-react';
import { StatusNotice } from '@/components/genealogy/StatusIndicator';
import ApprovalSignModal, { postAct } from '@/pages/Batch-Processing/Recipe/ApprovalSignModal';
import { formatDateTime } from '@/utils/datetime';
import StatCard from '@/components/StatCard';
import { getBatchStatusStyle } from '@/utils/operationExecutionStatus';
import { fetchMbmrOptions } from './bmrDetailsApi';
import './BmrDetails.css';

const { RangePicker } = DatePicker;

export function useBmrdVars() {
  const { token } = theme.useToken();
  return useMemo(
    () => ({
      '--bmrd-card': token.colorBgContainer,
      '--bmrd-border': token.colorBorderSecondary,
      '--bmrd-text': token.colorText,
      '--bmrd-muted': token.colorTextSecondary,
      '--bmrd-hover': token.colorFillQuaternary,
      '--bmrd-accent': token.colorPrimary,
      '--bmrd-accent-bg': token.colorPrimaryBg,
    }),
    [token]
  );
}

// presentational only: which icon a tile shows; the label, value and meaning are the server's
// the tile's accent is the project's batch-status colour for the state it counts
const TILE_STATUS = { running: 'running', pending_batches: 'scheduled', on_hold: 'hold', completed_total: 'completed', rejected: 'rejected' };
const TILE_ICON = { running: Activity, pending_batches: CalendarClock, on_hold: PauseCircle, completed_total: CheckCheck, rejected: XCircle };

export function BatchStateTiles({ tiles, error, onRetry, onOpen, activeKey }) {
  if (error) {
    return (
      <div className="bmrd-tiles" style={{ display: 'block' }}>
        <StatusNotice state="error" title="The batch counts could not be loaded">
          <Button size="small" onClick={onRetry} style={{ marginTop: 6 }}>Retry</Button>
        </StatusNotice>
      </div>
    );
  }
  if (!tiles) {
    return (
      <div className="bmrd-tiles" aria-busy="true">
        <Skeleton active paragraph={false} />
      </div>
    );
  }
  // the read finished but the server sent no tiles: show nothing, not a skeleton that never resolves
  if (tiles.length === 0) return null;
  return (
    <div className="bmrd-tiles" role="group" aria-label="Batch counts" data-testid="bmrd-tiles">
      {tiles.map((t) => {
        const Icon = TILE_ICON[t.key] || Activity;
        const accent = getBatchStatusStyle(TILE_STATUS[t.key] || '').color;
        return (
          <div
            key={t.key}
            role="button"
            tabIndex={0}
            className="bmrd-tile"
            title={t.definition || undefined}
            aria-pressed={activeKey === t.key}
            aria-label={`${t.label}: ${t.value}`}
            onClick={() => onOpen(t)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(t))}
            data-testid={`bmrd-tile-${t.key}`}
          >
            <StatCard label={t.label} value={t.value} icon={<Icon size={20} />} bgColor={`${accent}22`} iconColor={accent} />
          </div>
        );
      })}
    </div>
  );
}

/** MBMR + schedule-date range + search; `value` = { recipeId, from, to, search }, `onChange(next)` on apply. */
export function BatchListFilters({ value, onChange, moduleType, mbmrLabel, showMbmr = true, showDates = true, showSearch = true }) {
  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(false);
  const [search, setSearch] = useState(value.search || '');
  useEffect(() => setSearch(value.search || ''), [value.search]);
  useEffect(() => {
    if (!showMbmr) return undefined;
    let live = true;
    setOptionsError(false);
    fetchMbmrOptions({ module_type: moduleType })
      .then((rows) => live && setOptions(rows))
      .catch(() => live && setOptionsError(true));
    return () => { live = false; };
  }, [moduleType, showMbmr]);

  const range = value.from && value.to ? [toPickerValue(value.from), toPickerValue(value.to)] : null;
  return (
    <>
      {showMbmr && (
        <Select
          showSearch
          allowClear
          aria-label={`Filter by ${mbmrLabel}`}
          placeholder={optionsError ? `${mbmrLabel} list unavailable` : `All ${mbmrLabel}s`}
          status={optionsError ? 'warning' : undefined}
          style={{ width: 220 }}
          value={value.recipeId || undefined}
          optionFilterProp="label"
          options={options.map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => onChange({ ...value, recipeId: v || null })}
        />
      )}
      {showDates && (
        <RangePicker
          aria-label="Scheduled between"
          placeholder={['Scheduled from', 'Scheduled to']}
          format="YYYY-MM-DD"
          value={range}
          onChange={(_v, s) =>
            onChange({ ...value, from: (s && s[0]) || null, to: (s && s[1]) || null })
          }
        />
      )}
      {showSearch && <Input.Search
        allowClear
        aria-label="Search batch, MBMR or product"
        placeholder="Search batch, record or product"
        style={{ width: 260, maxWidth: '100%' }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!e.target.value) onChange({ ...value, search: '' });
        }}
        onSearch={(v) => onChange({ ...value, search: (v || '').trim() })}
      />}
    </>
  );
}

/**
 * The Completed list's Status, Released by and Released on filters. The options are what the server says is present
 * (`filter_options`); `value` = { status, releasedBy, releasedFrom, releasedTo }, `onChange(next)` on apply.
 */
export function CompletedStatusFilters({ value, onChange, options }) {
  const words = options?.status_words || [];
  const people = options?.released_by || [];
  const range = value.releasedFrom && value.releasedTo ? [toPickerValue(value.releasedFrom), toPickerValue(value.releasedTo)] : null;
  return (
    <>
      <Select
        allowClear
        aria-label="Filter by status"
        placeholder="All statuses"
        style={{ width: 220 }}
        value={value.status || undefined}
        options={words.map((w) => ({ value: w, label: w }))}
        onChange={(v) => onChange({ ...value, status: v || null })}
      />
      <Select
        showSearch
        allowClear
        aria-label="Filter by released by"
        placeholder="Released by anyone"
        style={{ width: 220 }}
        value={value.releasedBy || undefined}
        optionFilterProp="label"
        options={people.map((p) => ({ value: p, label: p }))}
        onChange={(v) => onChange({ ...value, releasedBy: v || null })}
      />
      <RangePicker
        aria-label="Released between"
        placeholder={['Released from', 'Released to']}
        format="YYYY-MM-DD"
        value={range}
        onChange={(_v, s) => onChange({ ...value, releasedFrom: (s && s[0]) || null, releasedTo: (s && s[1]) || null })}
      />
    </>
  );
}

// RangePicker wants dayjs values; the filter keeps plain YYYY-MM-DD strings
const toPickerValue = (s) => dayjs(s);

export function BatchRowActions({ record, onViewRecord, onViewWorkflow, onTerminate, canTerminate }) {
  const menu = canTerminate
    ? {
        items: [{ key: 'terminate', danger: true, icon: <StopCircle size={16} />, label: 'Terminate batch…' }],
        onClick: ({ key }) => key === 'terminate' && onTerminate(record),
      }
    : null;
  return (
    <div className="bmrd-actions">
      <Tooltip title="View record">
        <Button icon={<Eye size={16} />} onClick={() => onViewRecord(record)} aria-label={`View record ${record.batch_number}`} />
      </Tooltip>
      <Tooltip title="View workflow">
        <Button icon={<Workflow size={16} />} onClick={() => onViewWorkflow(record)} aria-label={`View workflow of ${record.batch_number}`} />
      </Tooltip>
      {menu && (
        <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
          <Button icon={<MoreVertical size={16} />} aria-label={`More actions for ${record.batch_number}`} />
        </Dropdown>
      )}
    </div>
  );
}

/** A refused act's facts, in the server's words: its code as the title, its message, and any hold facts it sent. */
export function ActRefusal({ refusal }) {
  if (!refusal) return null;
  const hold = refusal.hold || refusal.data?.hold || {};
  const facts = [
    hold.placed_by || refusal.placed_by ? `Placed by ${hold.placed_by || refusal.placed_by}` : null,
    hold.at || refusal.at ? formatDateTime(hold.at || refusal.at) : null,
    hold.reason || refusal.reason ? `Reason: ${hold.reason || refusal.reason}` : null,
    refusal.batch_status ? `Batch status: ${refusal.batch_status}` : null,
  ].filter(Boolean);
  return (
    <div className="bmrd-refusal" data-testid="bmrd-refusal">
      <StatusNotice state="error" title={refusal.code || 'Refused'}>
        <div>{refusal.message}</div>
        {facts.length ? <div style={{ marginTop: 4 }}>{facts.join(' · ')}</div> : null}
      </StatusNotice>
    </div>
  );
}

/** Terminate: reason + e-signature in one dialog and one signed request (the existing act, unchanged). */
export function TerminateBatchModal({ record, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  useEffect(() => setRefusal(null), [record]);
  const submit = async ({ username, password, comment }) => {
    setBusy(true);
    setRefusal(null);
    try {
      const res = await postAct('terminate_batch_details', {
        batch_number: record.batch_number,
        comment,
        esign: { username, password, comment },
      });
      setBusy(false);
      onDone(res);
    } catch (err) {
      const body = err?.response?.data || {};
      setRefusal({ ...body, message: body.message || body.detail || 'The terminate was refused.' });
      setBusy(false);
    }
  };
  return (
    <ApprovalSignModal
      open={!!record}
      action="Terminate"
      busy={busy}
      title={`Terminate ${record?.batch_number || ''}`}
      meaning="Ends the batch. Every record is kept; it cannot be undone."
      requireReason
      reasonLabel="Reason for terminating"
      onCancel={onClose}
      onSubmit={submit}
    >
      <ActRefusal refusal={refusal} />
    </ApprovalSignModal>
  );
}
